import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService, getToken, isAuthenticated } from '../../../services';
import { utils } from '../../../utils';
import FormLayout from '../../../components/Common/FormLayout';

interface ChecklistItemData {
  id: number;
  descricao: string;
  categoria: string | null;
  tipo: string;
  obrigatorio: boolean;
  ordem: number;
  grupo_item_ordem?: number;
}

const ChecklistGroupEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo: 'Veículo',
    categoria: '',
    ativo: true,
    ordem: 0
  });

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  // Estado para gestão de itens
  const [linkedItems, setLinkedItems] = useState<ChecklistItemData[]>([]);
  const [availableItems, setAvailableItems] = useState<ChecklistItemData[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);
  const [isLoadingAvailable, setIsLoadingAvailable] = useState(false);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [activeTab, setActiveTab] = useState<'info' | 'itens'>('info');
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // Verificar autenticação ao montar
  useEffect(() => {
    const token = getToken();
    const authenticated = isAuthenticated();

    if (!token || !authenticated) {
      utils.showNotification('Você precisa estar autenticado para acessar esta página.', { type: 'warning' });
      navigate('/login');
      return;
    }

    loadGroupData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, id]);

  const loadGroupData = async () => {
    try {
      setIsLoadingData(true);

      const response = await apiService.getChecklistGroup(parseInt(id!));

      if (response && response.data) {
        setFormData({
          nome: response.data.nome || '',
          descricao: response.data.descricao || '',
          tipo: response.data.tipo || 'Veículo',
          categoria: response.data.categoria || '',
          ativo: response.data.status === 'Ativo',
          ordem: response.data.ordem || 0
        });
      }

      // Carregar itens vinculados
      await loadLinkedItems();
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao carregar dados do grupo', { type: 'error' });
      navigate('/grupos-checklist');
    } finally {
      setIsLoadingData(false);
    }
  };

  const loadLinkedItems = useCallback(async () => {
    try {
      setIsLoadingItems(true);
      const response = await apiService.getChecklistGroupItems(parseInt(id!));
      if (response && response.data) {
        setLinkedItems(response.data);
      }
    } catch (error: any) {
      console.error('Erro ao carregar itens do grupo:', error);
      setLinkedItems([]);
    } finally {
      setIsLoadingItems(false);
    }
  }, [id]);

  const loadAvailableItems = useCallback(async (search?: string) => {
    try {
      setIsLoadingAvailable(true);
      const response = await apiService.getChecklistGroupAvailableItems(parseInt(id!), {
        search: search || undefined
      });
      if (response && response.data) {
        setAvailableItems(response.data);
      }
    } catch (error: any) {
      console.error('Erro ao carregar itens disponíveis:', error);
      setAvailableItems([]);
    } finally {
      setIsLoadingAvailable(false);
    }
  }, [id]);

  // Carregar itens disponíveis quando abre a aba de itens (com debounce na busca)
  useEffect(() => {
    if (activeTab === 'itens' && !isLoadingData) {
      const timer = setTimeout(() => {
        loadAvailableItems(searchAvailable);
      }, searchAvailable ? 400 : 0);
      return () => clearTimeout(timer);
    }
  }, [activeTab, isLoadingData, loadAvailableItems, searchAvailable]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : (type === 'number' ? parseInt(value) || 0 : value)
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }
    if (!formData.tipo.trim()) {
      newErrors.tipo = 'Tipo é obrigatório';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const token = localStorage.getItem('confereai_token');
    if (!token) {
      utils.showNotification('Sessão expirada. Por favor, faça login novamente.', { type: 'error' });
      navigate('/login');
      return;
    }

    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);

    const payload = {
      nome: formData.nome,
      descricao: formData.descricao || undefined,
      tipo: formData.tipo,
      categoria: formData.categoria || undefined,
      ordem: formData.ordem,
      status: formData.ativo ? 'Ativo' : 'Inativo'
    };

    try {
      const response = await apiService.updateChecklistGroup(parseInt(id!), payload);

      if (response.success) {
        utils.showNotification('Grupo de checklist atualizado com sucesso!', { type: 'success' });
        navigate('/grupos-checklist');
      } else {
        throw new Error(response.message || 'Erro ao atualizar grupo');
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao atualizar grupo', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    const fakeEvent = {
      preventDefault: () => {}
    } as React.FormEvent;
    handleSubmit(fakeEvent);
  };

  const handleCancel = () => {
    navigate('/grupos-checklist');
  };

  // === Funções de gestão de itens ===

  const handleAddItem = async (itemId: number) => {
    try {
      const ordem = linkedItems.length;
      await apiService.addItemToGroup(parseInt(id!), itemId, ordem);
      utils.showNotification('Item vinculado ao grupo com sucesso!', { type: 'success' });
      await loadLinkedItems();
      await loadAvailableItems(searchAvailable);
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao vincular item', { type: 'error' });
    }
  };

  const handleRemoveItem = async (itemId: number) => {
    try {
      await apiService.removeItemFromGroup(parseInt(id!), itemId);
      utils.showNotification('Item desvinculado do grupo.', { type: 'success' });
      await loadLinkedItems();
      await loadAvailableItems(searchAvailable);
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao desvincular item', { type: 'error' });
    }
  };

  const handleSearchAvailable = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchAvailable(e.target.value);
  };

  // === Funções de reordenação ===

  const handleMoveItem = async (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === linkedItems.length - 1)
    ) {
      return;
    }

    const newItems = [...linkedItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    // Trocar posições
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];

    // Atualizar estado local imediatamente para feedback visual
    setLinkedItems(newItems);

    // Salvar a nova ordem no backend
    try {
      const itens = newItems.map((item, idx) => ({
        itemId: item.id,
        ordem: idx
      }));
      await apiService.setGroupItems(parseInt(id!), itens);
    } catch (error: any) {
      // Se falhar, reverter
      await loadLinkedItems();
      utils.showNotification('Erro ao reordenar itens', { type: 'error' });
    }
  };

  const handleSaveOrder = async () => {
    try {
      setIsSavingOrder(true);
      const itens = linkedItems.map((item, idx) => ({
        itemId: item.id,
        ordem: idx
      }));
      await apiService.setGroupItems(parseInt(id!), itens);
      utils.showNotification('Ordem dos itens salva com sucesso!', { type: 'success' });
      await loadLinkedItems();
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao salvar ordem', { type: 'error' });
    } finally {
      setIsSavingOrder(false);
    }
  };

  const getTipoBadgeColor = (tipo: string) => {
    switch (tipo) {
      case 'booleano': return '#27ae60';
      case 'texto': return '#2980b9';
      case 'numero': return '#f39c12';
      case 'selecao': return '#8e44ad';
      case 'multipla_escolha': return '#e74c3c';
      case 'foto': return '#1abc9c';
      case 'assinatura': return '#34495e';
      default: return '#95a5a6';
    }
  };

  if (isLoadingData) {
    return (
      <FormLayout
        title="Editar Grupo de Checklist"
        subtitle="Carregando dados..."
        onCancel={handleCancel}
        onSave={handleSave}
        saveButtonText="Salvar"
        isLoading={true}
        showActions={false}
      >
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <span className="loading"></span>
          <p>Carregando dados do grupo...</p>
        </div>
      </FormLayout>
    );
  }

  return (
    <FormLayout
      title="Editar Grupo de Checklist"
      subtitle="Edição de Grupo de Checklist"
      onCancel={handleCancel}
      onSave={handleSave}
      saveButtonText="Salvar Alterações"
      isLoading={isLoading}
      showActions={false}
    >
      {/* Abas */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
        <button
          type="button"
          onClick={() => setActiveTab('info')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'info' ? '#fff' : 'transparent',
            borderBottom: activeTab === 'info' ? '3px solid #04C64F' : '3px solid transparent',
            color: activeTab === 'info' ? '#04C64F' : '#666',
            fontWeight: activeTab === 'info' ? 'bold' : 'normal',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          <i className="fas fa-info-circle" style={{ marginRight: '8px' }}></i>
          Informações do Grupo
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('itens')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeTab === 'itens' ? '#fff' : 'transparent',
            borderBottom: activeTab === 'itens' ? '3px solid #04C64F' : '3px solid transparent',
            color: activeTab === 'itens' ? '#04C64F' : '#666',
            fontWeight: activeTab === 'itens' ? 'bold' : 'normal',
            cursor: 'pointer',
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          <i className="fas fa-list-check" style={{ marginRight: '8px' }}></i>
          Itens do Grupo ({linkedItems.length})
        </button>
      </div>

      {/* Aba: Informações do Grupo */}
      {activeTab === 'info' && (
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              Informações do Grupo
            </h3>

            <div className="info-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
              <div className="form-group">
                <label className="form-label">Nome *</label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className={`form-control ${errors.nome ? 'error' : ''}`}
                  placeholder="Ex: Inspeção Visual Externa"
                />
                {errors.nome && <span className="error-message">{errors.nome}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Tipo *</label>
                <select
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleInputChange}
                  className={`form-control ${errors.tipo ? 'error' : ''}`}
                >
                  <option value="Veículo">Veículo</option>
                  <option value="Documental">Documental</option>
                  <option value="Equipamento">Equipamento</option>
                  <option value="Operacional">Operacional</option>
                  <option value="Manutenção">Manutenção</option>
                </select>
                {errors.tipo && <span className="error-message">{errors.tipo}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Categoria</label>
                <select
                  name="categoria"
                  value={formData.categoria}
                  onChange={handleInputChange}
                  className="form-control"
                >
                  <option value="">Selecione uma categoria</option>
                  <option value="Segurança">Segurança</option>
                  <option value="Mecânica">Mecânica</option>
                  <option value="Elétrica">Elétrica</option>
                  <option value="Hidráulica">Hidráulica</option>
                  <option value="Estrutural">Estrutural</option>
                  <option value="Legal">Legal</option>
                  <option value="Operacional">Operacional</option>
                  <option value="Manutenção">Manutenção</option>
                  <option value="Preventiva">Preventiva</option>
                  <option value="Corretiva">Corretiva</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Ordem</label>
                <input
                  type="number"
                  name="ordem"
                  value={formData.ordem}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Ex: 1"
                  min="0"
                />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: '20px' }}>
              <label className="form-label">Descrição</label>
              <textarea
                name="descricao"
                value={formData.descricao}
                onChange={handleInputChange}
                className="form-control"
                placeholder="Descrição do grupo de checklist..."
                rows={4}
              />
            </div>

            <div className="form-group" style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                name="ativo"
                id="ativo"
                checked={formData.ativo}
                onChange={handleInputChange}
                style={{ width: 'auto', margin: 0 }}
              />
              <label htmlFor="ativo" className="form-label" style={{ margin: 0 }}>
                Grupo Ativo
              </label>
            </div>
          </div>

          {/* Ações do Formulário */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="loading"></span>
                  Salvando...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  Salvar Alterações
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Aba: Itens do Grupo */}
      {activeTab === 'itens' && (
        <div>
          {/* Itens vinculados */}
          <div style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', borderBottom: '2px solid #04C64F', paddingBottom: '10px' }}>
              <h3 style={{ margin: 0, color: '#2c3e50' }}>
                <i className="fas fa-link" style={{ marginRight: '8px' }}></i>
                Itens Vinculados ({linkedItems.length})
              </h3>
              {linkedItems.length > 1 && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <small style={{ color: '#7f8c8d', fontSize: '12px' }}>
                    <i className="fas fa-arrows-alt-v" style={{ marginRight: '4px' }}></i>
                    Use as setas para reordenar
                  </small>
                  <button
                    type="button"
                    onClick={handleSaveOrder}
                    disabled={isSavingOrder}
                    style={{
                      padding: '5px 12px',
                      border: '1px solid #04C64F',
                      borderRadius: '6px',
                      backgroundColor: '#04C64F',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {isSavingOrder ? (
                      <><span className="loading" style={{ width: '12px', height: '12px' }}></span> Salvando...</>
                    ) : (
                      <><i className="fas fa-save"></i> Salvar Ordem</>
                    )}
                  </button>
                </div>
              )}
            </div>

            {isLoadingItems ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <span className="loading"></span>
                <p>Carregando itens...</p>
              </div>
            ) : linkedItems.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '30px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '2px dashed #dee2e6'
              }}>
                <i className="fas fa-inbox" style={{ fontSize: '2rem', color: '#adb5bd', marginBottom: '10px', display: 'block' }}></i>
                <p style={{ color: '#6c757d', margin: 0 }}>
                  Nenhum item vinculado a este grupo ainda.
                </p>
                <p style={{ color: '#adb5bd', fontSize: '13px', margin: '5px 0 0' }}>
                  Use a seção abaixo para adicionar itens de checklist.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {linkedItems.map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: '#fff',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      transition: 'box-shadow 0.2s'
                    }}
                  >
                    {/* Controles de ordem */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginRight: '12px' }}>
                      <button
                        type="button"
                        onClick={() => handleMoveItem(index, 'up')}
                        disabled={index === 0}
                        style={{
                          background: 'none',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          cursor: index === 0 ? 'not-allowed' : 'pointer',
                          padding: '2px 6px',
                          fontSize: '10px',
                          color: index === 0 ? '#ccc' : '#495057',
                          opacity: index === 0 ? 0.4 : 1
                        }}
                        title="Mover para cima"
                      >
                        <i className="fas fa-chevron-up"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveItem(index, 'down')}
                        disabled={index === linkedItems.length - 1}
                        style={{
                          background: 'none',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          cursor: index === linkedItems.length - 1 ? 'not-allowed' : 'pointer',
                          padding: '2px 6px',
                          fontSize: '10px',
                          color: index === linkedItems.length - 1 ? '#ccc' : '#495057',
                          opacity: index === linkedItems.length - 1 ? 0.4 : 1
                        }}
                        title="Mover para baixo"
                      >
                        <i className="fas fa-chevron-down"></i>
                      </button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: '#04C64F',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        flexShrink: 0
                      }}>
                        {index + 1}
                      </span>
                      <div>
                        <div style={{ fontWeight: '500', color: '#2c3e50' }}>
                          {item.descricao}
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                          {item.categoria && (
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#e8f5e9',
                              color: '#2e7d32'
                            }}>
                              {item.categoria}
                            </span>
                          )}
                          <span style={{
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            backgroundColor: getTipoBadgeColor(item.tipo) + '20',
                            color: getTipoBadgeColor(item.tipo)
                          }}>
                            {item.tipo}
                          </span>
                          {item.obrigatorio && (
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              backgroundColor: '#fce4ec',
                              color: '#c62828'
                            }}>
                              Obrigatório
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(item.id)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #e74c3c',
                        borderRadius: '6px',
                        backgroundColor: '#fff',
                        color: '#e74c3c',
                        cursor: 'pointer',
                        fontSize: '12px',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Desvincular item do grupo"
                    >
                      <i className="fas fa-times"></i>
                      Remover
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Adicionar itens */}
          <div>
            <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #3498db', paddingBottom: '10px' }}>
              <i className="fas fa-plus-circle" style={{ marginRight: '8px', color: '#3498db' }}></i>
              Adicionar Itens ao Grupo
            </h3>

            <div style={{ marginBottom: '15px' }}>
              <input
                type="text"
                value={searchAvailable}
                onChange={handleSearchAvailable}
                className="form-control"
                placeholder="Buscar itens por descrição ou categoria..."
                style={{ maxWidth: '500px' }}
              />
            </div>

            {isLoadingAvailable ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <span className="loading"></span>
                <p>Buscando itens disponíveis...</p>
              </div>
            ) : availableItems.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '25px',
                backgroundColor: '#fff3e0',
                borderRadius: '8px',
                border: '1px solid #ffe0b2'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '1.5rem', color: '#f57c00', marginBottom: '8px', display: 'block' }}></i>
                <p style={{ color: '#e65100', margin: 0, fontWeight: '500' }}>
                  Nenhum item disponível para vincular.
                </p>
                <p style={{ color: '#bf360c', fontSize: '13px', margin: '5px 0 0' }}>
                  Crie novos itens de checklist na seção <a href="/itens-checklist/novo" style={{ color: '#1565c0' }}>Itens de Checklist</a> e depois vincule aqui.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '400px', overflowY: 'auto' }}>
                {availableItems.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 16px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: '#495057' }}>
                        {item.descricao}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                        {item.categoria && (
                          <span style={{
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32'
                          }}>
                            {item.categoria}
                          </span>
                        )}
                        <span style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          backgroundColor: getTipoBadgeColor(item.tipo) + '20',
                          color: getTipoBadgeColor(item.tipo)
                        }}>
                          {item.tipo}
                        </span>
                        {item.obrigatorio && (
                          <span style={{
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            backgroundColor: '#fce4ec',
                            color: '#c62828'
                          }}>
                            Obrigatório
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddItem(item.id)}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #04C64F',
                        borderRadius: '6px',
                        backgroundColor: '#04C64F',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '12px',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="Vincular item ao grupo"
                    >
                      <i className="fas fa-plus"></i>
                      Adicionar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Botão de voltar */}
          <div className="form-actions" style={{ marginTop: '30px' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
            >
              Voltar para Lista
            </button>
          </div>
        </div>
      )}
    </FormLayout>
  );
};

export default ChecklistGroupEdit;
